import type { CardProvider } from './card-capture.js';
import type { Currency } from './currency.js';

/**
 * The provider-neutral shape of a hosted checkout —
 * docs/plans/06-portal-and-field.md §6.2 block B. **The types and the refusals
 * live here; every adapter, every HTTP client and every credential lives in
 * `packages/server`**, because this file is bundled into the portal and a
 * provider secret must never be in a browser bundle
 * (docs/decisions/0053 §3, `.claude/rules/secrets.md`).
 *
 * There is exactly one operation: create a session and get back a URL to hand
 * a donor, plus the provider's own id for it. Verification and the normalized
 * "payment confirmed" event are block D's, and they are declared here for the
 * same reason — so the approval path, the capture, the reversal, the receipt
 * and the ledger never learn which provider called.
 */
export const PAYMENT_LINK_STATUSES = ['active', 'paid', 'expired', 'superseded'] as const;

export type PaymentLinkStatus = (typeof PAYMENT_LINK_STATUSES)[number];

/** A link that is not `active` can take no money, whatever its URL still resolves to. */
export function isLivePaymentLink(status: PaymentLinkStatus, expiresAt: Date, now: Date): boolean {
  return status === 'active' && expiresAt.getTime() > now.getTime();
}

/**
 * What the provider is asked for. The amount and the currency are the
 * donation's own, already frozen, so nothing is converted at the moment of
 * payment (docs/decisions/0013, docs/decisions/0022).
 *
 * `reference` is the subject the capture will name — a donation's id, or a
 * portal order's — carried through checkout so the callback can find it
 * without trusting anything else in the event.
 */
export type CheckoutRequest = {
  reference: string;
  description: string;
  amountMinor: bigint;
  currency: Currency;
  returnUrl: string;
  cancelUrl: string;
  expiresAt: Date;
  donorEmail: string | null;
  /**
   * Derived from the `payment_links` row, so a creation retried after a lost
   * response never makes a second session at the provider.
   */
  idempotencyKey: string;
  /**
   * Ask the provider to keep the method the donor pays with, so that later
   * periods of a pledge can be charged against it —
   * docs/plans/06-portal-and-field.md §6.4. It is still hosted checkout: the
   * donor authorises a real payment on the provider's own page and the
   * provider, not BTSis, holds whatever it stored. Absent on every one-off
   * checkout, which asks for nothing to be kept.
   */
  storePaymentMethod?: boolean;
};

/** What a provider answers: a URL for the donor, and the provider's own id for the session. */
export type CheckoutSession = {
  provider: CardProvider;
  providerSessionId: string;
  url: string;
};

export const PAYMENT_CHECKOUT_UNAVAILABLE = 'PAYMENT_CHECKOUT_UNAVAILABLE';
export const PAYMENT_CHECKOUT_REFUSED = 'PAYMENT_CHECKOUT_REFUSED';
export const PAYMENT_PROVIDER_ENVIRONMENT_MISMATCH = 'PAYMENT_PROVIDER_ENVIRONMENT_MISMATCH';

/** Every reason a hosted checkout can fail to be created, provider-neutral. */
export const PAYMENT_CHECKOUT_REFUSALS = [
  PAYMENT_CHECKOUT_UNAVAILABLE,
  PAYMENT_CHECKOUT_REFUSED,
  PAYMENT_PROVIDER_ENVIRONMENT_MISMATCH,
] as const;

export type PaymentCheckoutRefusal = (typeof PAYMENT_CHECKOUT_REFUSALS)[number];

/**
 * A provider would not open a session. It carries **no provider body and no
 * credential** — only the neutral reason and the provider's own error code
 * where it published one, because the message a provider returns can echo what
 * was sent to it.
 */
export class CheckoutRefusedError extends Error {
  readonly reason: PaymentCheckoutRefusal;
  readonly provider: CardProvider;
  readonly providerCode: string | null;

  constructor(
    reason: PaymentCheckoutRefusal,
    provider: CardProvider,
    providerCode: string | null = null,
  ) {
    super(`${provider} refused to open a checkout session: ${reason}`);
    this.name = 'CheckoutRefusedError';
    this.reason = reason;
    this.provider = provider;
    this.providerCode = providerCode;
  }
}
